import React from 'react';
import { History } from 'history';
import { FormattedMessage, injectIntl, WrappedComponentProps} from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import without from 'lodash/without';
import MediaQuery from 'react-responsive';
import memoize from 'memoize-one';
import cn from 'classnames';
import qs from 'querystring';
import { withRouter } from "react-router";
import { QdsButton } from '@quartzds/core-react';

import {
  send,
  comparatorPageLoadEvent,
  documentDownloadEvent,
  isAnalyticsEnabled,
  fetchers,
  getParamValueFromURL,
} from '@pes-ui/utils';

import { CustomLinkLeftArrow } from './components/CustomLinkLeftArrow';
import DesktopProducts from './components/DesktopProducts';
import MobileProducts from './components/MobileProducts';
import { Page } from './components/ui/Page';
import ExportTo from './components/ui/Select';
import { withNotificationStorageContext } from './hocs/NotificationStorage';
import { makeId } from './utils/compareSessionId';

import {
    MOBILE_BREAKPOINT_MAX,
    URL_PARTNUMBERS_DIVIDER,
    LOCAL_STORAGE_PARTNUMBER_DEVIDER,
    COMPARISON_LOCAL_STORAGE_NAME,
    APC_COMPARISON_LOCAL_STORAGE_NAME,
    APC_COMPARISON_SESSION_ID,
    COMPARISON_SESSION_ID,
    NOT_AVAILABLE_PRODUCTS,
    DESKTOP_BREAKPOINT_MIN,
    BUNDLE_REGEXP,
} from './config/consts';
import { Loader } from './components/ui/Loader';
import { NoItemsComponent } from './components/NoItemsComponent';
import {
    excludeRedundantCategoryParameters,
    MappedProduct,
    MappedProducts,
    mapProductsWithCategories,
} from './utils/mappers';
import { SiteInfoApi } from '@pes-ui/utils/lib/types/api/site-info-api';
import { ProductsApi } from '@pes-ui/utils/lib/types/api/products-api';
import { ProductsParametersApi } from '@pes-ui/utils/lib/types/api/products-parameters-api';

import styles from './index.module.scss';
import { ProductInfoApi } from '@pes-ui/utils/lib/types/api/products-info-api';
import { CommerceConnectorApi } from '@pes-ui/utils/lib/types/api/commerce-connector-api';
import { TNotificationItem } from '@pes-ui/utils/lib/types/notifications';
import { ENotificationType, EStatus } from '@pes-ui/utils/lib/constants';
import { ChannelSightApi } from '@pes-ui/utils/lib/types/api/channel-sight-api';
import { ProductAvailabilityApi } from '@pes-ui/utils/lib/types/api/product-availability-api';
import { ApcCtaApi } from '@pes-ui/utils/lib/types/api/apc-cta-api';

import './index.scss';
import { TProductsRatings } from '@pes-ui/components/dist/collection/globals/types';
import { isHostnameContainsCC, parseBundleFromString } from './utils';

import { ComparisonBundleProducts } from "@pes-ui/utils/lib/types/api/comparison-bundle-products-api";
import { getComparatorPageURL } from './utils'
import type { ExtendedBaseRouteProps } from './types';
import { ComparatorRuntimeInfo } from '@pes-ui/utils/lib/types/comparator';

const partNumbersToArray = memoize((partNumbersString) => {
    if (!partNumbersString) {
        return [];
    }

    return partNumbersString.split(URL_PARTNUMBERS_DIVIDER);
});

declare global {
    interface Window {
        bvKey?: string;
        isV3?: boolean;
        isAsco?:boolean;
        isApc?:boolean;
    }
}

type Props = ExtendedBaseRouteProps & {
    history: History;
    bazaarVoiceEnabled?: boolean;
    match: {
        params: {
            compareSessionId: string;
            skus?: string;
        };
    };
    notification: {
        stack: [];
        addNotification: (params: {
            type?: ENotificationType;
            status: EStatus;
            message: string;
            productsList?: TNotificationItem[];
            buttonText?: string;
        }) => void;
        removeNotification: () => void;
    };
} & WrappedComponentProps;

type State = {
    productsRatings: TProductsRatings;
    siteInfo: SiteInfoApi.SiteInfo;
    commerceConnector: CommerceConnectorApi.GetCommerceConnectorResponse;
    myProductsList: string[];
    products: MappedProducts;
    productsFeed: ChannelSightApi.GetChannelSightResponse;
    labels: ProductsApi.Labels;
    downloadButtonLabels: ProductInfoApi.DownloadButtonLabels;
    isLoading: boolean;
    productsUIAAS: ProductsApi.Products;
    toggleSectionsState: Record<string, boolean>;
    showDevPrioritiesInfo: boolean;
    hideSimilarFeatures: boolean;
    highlightDifferences: boolean;
    hideSimilaritiesCalculated: boolean;
    partNumbersList: string;
    categories: ProductsParametersApi.Sections;
    keyCategories: ProductsParametersApi.Sections;
    productsUIAASinfo: ProductInfoApi.GetProductsInfoResponse['productAdditionalInfos'];
    runtimeInfo: ComparatorRuntimeInfo;
    apcCta: ApcCtaApi.ApcCtaResponse;
    isAPCSite: boolean;
    productAvailabilityInfo: ProductAvailabilityApi.ProductAvailabilityResponse;
    bundledProducts: ComparisonBundleProducts.BundledProductListType[];
};

class ComparePage extends React.Component<Props, State> {
    private timer;
    constructor(props) {
        super(props);
        this.state = {
            productsRatings: {},
            siteInfo: null,
            commerceConnector: null,
            productsFeed: [],
            myProductsList: [],
            isLoading: false,
            products: {},
            productsUIAAS: [],
            labels: null,
            downloadButtonLabels: {},
            toggleSectionsState: {},
            showDevPrioritiesInfo: false,
            hideSimilarFeatures: true,
            highlightDifferences: false,
            hideSimilaritiesCalculated: false,
            partNumbersList: '',
            categories: [],
            keyCategories: [],
            productsUIAASinfo: [],
            runtimeInfo: null,
            apcCta: null,
            isAPCSite: false,
            productAvailabilityInfo: null,
            bundledProducts: []
        };
    }

    componentDidMount() {
        const { countryCode, languageCode, history, contextRoot } = this.props;
        const { compareSessionId } = this.props.match.params;
        const baseURL = getComparatorPageURL({
            countryCode: this.props.countryCode,
            languageCode: this.props.languageCode,
            pageRoute: this.props.pageRoute,
        });

        if (contextRoot) {
            const compareSessionIdFromLocalStorage_APC = localStorage.getItem(APC_COMPARISON_SESSION_ID);
            const partNumberFromLocalStorage_APC = localStorage.getItem(APC_COMPARISON_LOCAL_STORAGE_NAME);
            const partNumbers_APC = this.getPartNumbers();

            if (compareSessionId === compareSessionIdFromLocalStorage_APC && !partNumbers_APC && Boolean(partNumberFromLocalStorage_APC)) {
                const newPartNumbers_APC = JSON.parse(partNumberFromLocalStorage_APC);

                history.push(`${baseURL}/${compareSessionId}/${newPartNumbers_APC.join(URL_PARTNUMBERS_DIVIDER)}/`);
            }
        }
        else {
            const compareSessionIdFromLocalStorage = localStorage.getItem(COMPARISON_SESSION_ID);
            const partNumberFromLocalStorage = localStorage.getItem(`${COMPARISON_LOCAL_STORAGE_NAME}_${languageCode}_${countryCode.toUpperCase()}`);
            const partNumbers = this.getPartNumbers();

            if (compareSessionId === compareSessionIdFromLocalStorage && !partNumbers && Boolean(partNumberFromLocalStorage)) {
                const newPartNumbers = partNumberFromLocalStorage.split(LOCAL_STORAGE_PARTNUMBER_DEVIDER);

                history.push(`${baseURL}/${compareSessionId}/${newPartNumbers.join(URL_PARTNUMBERS_DIVIDER)}/`);
            }
        }

        this.timer = setTimeout( () => this.fetchProducts(this.getPartNumbers()), 0);
    }

    componentWillUnmount() {
        clearTimeout(this.timer);
    }
     
    getProductName (productValues, id) : string {
        return productValues[id]?.name || '';
    }

    getProductId (productValues, id) : string {
        return productValues[id]?.productId || '';
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.history.location !== this.props.history.location) {
            if (this.hasNewPartNumbers(prevProps)) {
                this.fetchProducts(this.getPartNumbers());
            }
        }

        if (isEmpty(prevState.products) && prevState.products !== this.state.products) {
            const productValues = [];
                for (let productId in this.state.products) {
                    productValues.unshift({productId, name: this.state.products[productId]?.name})
                }
    
                const comparatorPageLoadParam = {
                    productId1: this.getProductId (productValues, 0),
                    productId2: this.getProductId (productValues, 1),
                    productId3: this.getProductId (productValues, 2),
                    productId4: this.getProductId (productValues, 3),
                    productName1: this.getProductName (productValues, 0),
                    productName2: this.getProductName (productValues, 1),
                    productName3: this.getProductName (productValues, 2),
                    productName4: this.getProductName (productValues, 3),
                }

                send(comparatorPageLoadEvent, comparatorPageLoadParam);
            }
    }

    hasNewPartNumbers(prevProps) {
        const oldPartNumbers = partNumbersToArray(get(prevProps, 'match.params.skus', ''));
        const partNumbers = this.getPartNumbers();

        return without(partNumbers, ...oldPartNumbers).length > 0;
    }

    getPartNumbers() {
        return partNumbersToArray(get(this, 'props.match.params.skus', ''));
    }

    handleUpdateMyProductsList = async () => {
        const { contextRoot, countryCode, languageCode } = this.props;
        try {
            const myProductsResponse = await fetchers.getMyProducts({
                contextRoot,
                countryCode: !isHostnameContainsCC() ? countryCode : null,
                languageCode,
                params: {
                    t: makeId(13)
                },
            });

            if (!myProductsResponse && !myProductsResponse?.lists[0].products) {
                throw new Error("myProduct isn't exist");
            }

            this.setState({
                myProductsList: myProductsResponse?.lists[0]?.products?.map(
                    (product) => Object.keys(product)[0]
                )
            })
        } catch (error) {
            console.error(error);
        }
    };

    getProductsIdsToRemove = (partNumbersArr: string[], products: ProductsApi.Products): string[]  => {
        return partNumbersArr.reduce((acc, productNumber) => {
            let productId = productNumber
            if(productNumber.match(BUNDLE_REGEXP)){
                const bundleInfo = parseBundleFromString(productNumber);
                productId = bundleInfo.productId;
            }
            if (!products.find(product => (product.repositoryId === productId))) {
                acc.push(productNumber);
            }
            return acc;
        }, []);
    };

    getProductsToRemove = (productsIds: string[]): TNotificationItem[] => {
        return productsIds.map((product) => {
            if (product.match(BUNDLE_REGEXP)) {
                const { productId, bundledProductId, quantity } = parseBundleFromString(product);
                return {
                    products: [
                        {
                            title: productId,
                            amount: 1,
                        },
                        {
                            title: bundledProductId,
                            amount: Number(quantity),
                        },
                    ],
                };
            } else {
                return { products: [{ title: product, amount: 1 }] };
            }
        })
    };

    getProductsToRemoveNotAvailable = (notAvailableProducts: ProductsApi.Products): TNotificationItem[] => {
        return notAvailableProducts.map((product) => {
            const products = [];
            if (product.bundle) {
                products.push(
                    {
                        amount: 1,
                        title: product.description || product.repositoryId,
                        url: product.pdpUrl
                    },
                    {
                        amount: product.bundle.quantity,
                        title: product.bundle.valid ? product.bundle.description : product.bundle.repositoryId,
                        url: product.bundle.pdpUrl
                    }
                )
            } else {
                products.push({
                    title: product.description || product.repositoryId,
                });
            }
            return { products }
        })
    };

    getProductsIdsToRemoveNotAvailable = (notAvailableProducts: ProductsApi.Products): string[] => {
        return notAvailableProducts.map((product) => {
            if(product.bundle){
                return `${product.repositoryId}:(${product.bundle.quantity})${product.bundle.repositoryId}`;
            }
            return product.repositoryId;
        })
    }
    

    async fetchProducts(partNumbersArr) {
        const { contextRoot, countryCode, languageCode, bazaarVoiceEnabled } = this.props;

        const bundleIds: string[] = [];

        if (!partNumbersArr.length) {
            return Promise.resolve();
        }

        const partNumbersList = partNumbersArr.join(URL_PARTNUMBERS_DIVIDER);
        this.setState({ isLoading: true, partNumbersList });

        // If some requested products are unvailable, we put their ids here
        // this is used in notifications
        let productsToRemove: TNotificationItem[] = [];
        let productsToRemoveIds: string[] = [];
        let notAvailableProducts: ProductsApi.Products = [];
        let notAvailableProductsIds: string[] = [];
        try {
            const [
                productsUiaasResponse,
                parametersResponse,
                productsUiaasInfoResponse,
                comparisonBundledProductsResponse
            ] = await Promise.all([
                fetchers.getProductsFetcher({
                    contextRoot,
                    countryCode: !isHostnameContainsCC() ? countryCode : null,
                    languageCode,
                    params: { ids: partNumbersArr.join(',') },
                }),
                fetchers.getProductsParametersFetcher({
                    contextRoot,
                    countryCode: !isHostnameContainsCC() ? countryCode : null,
                    languageCode,
                    params: { 'product-ids': partNumbersList },
                }),
                fetchers.getProductsInfoFetcher({
                    contextRoot,
                    countryCode: !isHostnameContainsCC() ? countryCode : null,
                    languageCode,
                    params: { ids: partNumbersArr.join(',') },
                }),
                fetchers.getComparisonBundleProductsFetcher({
                    contextRoot,
                    countryCode: !isHostnameContainsCC() ? countryCode : null,
                    languageCode,
                    params: { 'product-ids': partNumbersArr.join('|') },
                })
            ]);

            productsUiaasResponse.products.forEach((product) => {
                if (product?.bundle) {
                    product.commercialReference = `${product.commercialReference}:(${product.bundle.quantity})${product.bundle.repositoryId}`;
                    bundleIds.push(product.bundle.repositoryId);
                }
            });

            const mergedProductsWithCategories = mapProductsWithCategories(
                productsUiaasResponse,
                parametersResponse
            );
            const { products } =  productsUiaasResponse;
            const { productsList } = comparisonBundledProductsResponse;
            const excludedRedundantCategoryParameters = excludeRedundantCategoryParameters(mergedProductsWithCategories);
            notAvailableProducts = products.filter( product => product.available === false || product.bundle?.valid === false);
            this.processResponse(excludedRedundantCategoryParameters);
            // If some products are not returned or some products are not available, throw an error and add notification.
            if (partNumbersArr.length !== products.length || notAvailableProducts.length > 0) {
                productsToRemoveIds = this.getProductsIdsToRemove(partNumbersArr, products);
                notAvailableProductsIds = this.getProductsIdsToRemoveNotAvailable(notAvailableProducts);
                productsToRemove = [
                    ...this.getProductsToRemove(productsToRemoveIds),
                    ...this.getProductsToRemoveNotAvailable(notAvailableProducts)
                ];

                throw new Error(NOT_AVAILABLE_PRODUCTS);
            }

            if (contextRoot) {
                localStorage.setItem(
                    APC_COMPARISON_LOCAL_STORAGE_NAME,
                    JSON.stringify(partNumbersArr)
                );
            }
            else {
                localStorage.setItem(
                    `${COMPARISON_LOCAL_STORAGE_NAME}_${languageCode}_${countryCode.toUpperCase()}`,
                    partNumbersArr.join(',')
                );
            }
            const downloadButtonLabels = {
                downloadButtonLabel: productsUiaasInfoResponse?.downloadButtonLabel,
                viewOnlineButtonLabel: productsUiaasInfoResponse?.viewOnlineButtonLabel,
                seeSoftwareButtonLabel: productsUiaasInfoResponse?.seeSoftwareButtonLabel,
            };

            const { purchaseOnAPCLabel } = productsUiaasInfoResponse?.purchaseOnAPCLabel && productsUiaasInfoResponse;
            this.setState({
                productsUIAAS: productsUiaasResponse.products,
                labels: {
                    ...productsUiaasResponse.labels,
                    ...productsUiaasInfoResponse?.bundleLabels,
                    purchaseOnAPCLabel
                },
                hideSimilarFeatures: parametersResponse.hideSimilarities,
                runtimeInfo: parametersResponse?.runtimeInfo,
                productsUIAASinfo:
                    productsUiaasInfoResponse.productAdditionalInfos,
                downloadButtonLabels,
                bundledProducts: productsList
            });

            this.generateToggleSectionsState();

        } catch (error: any) {
            this.setState({
                isLoading: false,
            });

            if (error.message === NOT_AVAILABLE_PRODUCTS) {
                if(productsToRemove.length > 0) {
                    const message = this.props.intl.formatMessage({
                        id: 'compare.errorMessage',
                        defaultMessage: 'Sorry, the following products could not be added to the comparison:'
                    });
                    this.props.notification.addNotification({
                        type: ENotificationType.PRODUCTS_LIST,
                        status: EStatus.ERROR,
                        message,
                        productsList: productsToRemove,
                        buttonText: '',
                    });
                }
                this.removeNotAvailableProducts([...productsToRemoveIds, ...notAvailableProductsIds]);
            } else {
                this.setState({
                    isLoading: true,
                },
                    () => window.open(`${window.location.origin}/maintenance/maintenance.html`, '_self')
                );
            }
        }

        // optional requiests
        try {
            const commerceConnectorResponse =
                    (await fetchers.getCommerceConnector({
                        contextRoot,
                        countryCode: !isHostnameContainsCC() ? countryCode : null,
                        languageCode,
                    }));
            this.setState({
                commerceConnector: commerceConnectorResponse,
                isAPCSite: commerceConnectorResponse?.options?.isApcSite,
            })

            const myProductsResponse =
                    (await fetchers.getMyProducts({
                        contextRoot,
                        countryCode: !isHostnameContainsCC() ? countryCode : null,
                        languageCode,
                        params: {
                            t: makeId(13)
                        },
                    }));
            const myProductsList = myProductsResponse?.lists[0]?.products?.map(
                (product) => Object.keys(product)[0]
            );
            this.setState({
                myProductsList
            })

            const { feedUrl, enableChannelSight, isApcSite, apcCtaUrl } =
            commerceConnectorResponse?.options ?? {};

            // ProductsData request
            if (enableChannelSight && feedUrl) {
                this.getProductsData(feedUrl, partNumbersArr);
            }

            //apcCta & productAvailablity requests
            if (isApcSite && apcCtaUrl) {
                this.getApcCta(contextRoot, countryCode, languageCode, partNumbersArr);
            }

                //BazaarVoice request
            if (bazaarVoiceEnabled && window?.bvKey) {
                this.getBazaarVoice(partNumbersArr, bundleIds);
            }

        } catch (error: any) {
            console.error(error.message);
        }
    }

    async getProductsData(feedUrl, partNumbersArr){
        try {
            const productsFeedResponse =
                    (await fetchers.getProductsDataFeed({
                        params: { feedUrl, productIds: partNumbersArr.join(',') },
                    })) || null;
                    this.setState({ productsFeed: productsFeedResponse });
        } catch(err) {
            console.error('Products Data Feed request failed');
        }
    }

    async getApcCta(contextRoot, countryCode, languageCode, partNumbersArr){
        try {
            const apcCtaResponse =
                    (await fetchers.getApcCtaFetcher({
                        contextRoot,
                        countryCode: !isHostnameContainsCC() ? countryCode : null,
                        languageCode,
                    })) || null;
            this.getProductAvailability(apcCtaResponse, countryCode, languageCode, partNumbersArr)
        } catch(err) {
            console.error('APC CTA request failed');
        }
    }

    async getProductAvailability(apcCtaResponse, countryCode, languageCode, partNumbersArr) {
        try {
            const productAvailabilityResponse =
            (await fetchers.getProductAvailabilityFetcher({
                apcHost: apcCtaResponse?.urls?.apcHost,
                params: {
                    partNumbersList: partNumbersArr.join('|').replace(/:\(\d+\)/g,'|'),
                    countryCode,
                    languageCode,
                },
            })) || null;

            this.setState({
                productAvailabilityInfo: productAvailabilityResponse,
                apcCta: apcCtaResponse,
            });
        } catch (err) {
            console.error('Product Availability request failed');
        }
    }

    async getBazaarVoice(partNumbersArr, bundleIds){
        const repositoryIds = partNumbersArr.map(id => id.split(':')[0]);
            const bazaarVoiceParams = qs.stringify({
                apiversion: '5.4',
                passkey: window.bvKey,
                filter: `productid:${[ ...repositoryIds, ...bundleIds ].join(',')}`,
                stats: 'Reviews',
            });
        try {
            this.processBazaarVoiceResponse(
                await fetchers.getBazaarVoiceFetcher(bazaarVoiceParams),
            );
        } catch(err) {
            console.error('BazaarVoice request failed');
        }
    }

    processBazaarVoiceResponse(ratingsData): void {
        const productsRatings = {};
        ratingsData.Results.forEach(({ ProductStatistics }) => {
            const {
                ProductId,
                ReviewStatistics: {
                    TotalReviewCount,
                    AverageOverallRating,
                    OverallRatingRange,
                },
            } = ProductStatistics;
            productsRatings[ProductId] = {
                totalQty: TotalReviewCount,
                rate: AverageOverallRating,
                range: OverallRatingRange,
            };
        });
        this.setState({ productsRatings });
    }

    processResponse = (
        data: ReturnType<typeof excludeRedundantCategoryParameters>,
    ) => {
        const updater: {
            isLoading: boolean;
            products: MappedProducts;
            categories: ProductsParametersApi.Sections;
            keyCategories: ProductsParametersApi.KeySections;
        } = {
            isLoading: false,
            products: {},
            categories: data.categories,
            keyCategories: data.keyCategories,
        };
        data.products.forEach(
            ({
                available,
                description,
                pictureUrl,
                pdpUrl,
                productCategories,
                commercialReference,
            }) => {
                updater.products[commercialReference] = {
                    available,
                    name: description,
                    productId: commercialReference,
                    partNumber: commercialReference,
                    image: pictureUrl,
                    url: pdpUrl,
                    productCategories,
                };
            },
        );
        this.setState(updater);
        sessionStorage.setItem('products', JSON.stringify(updater.products));
    };

    removeNotAvailableProducts = (partNumbersToRemove: string[]) => {
        const {
            contextRoot,
            countryCode,
            languageCode,
            match: {
                params: { compareSessionId },
            },
        } = this.props;

        const compareSessionIdFromLocalStorage = localStorage.getItem(COMPARISON_SESSION_ID);
        const compareSessionIdFromLocalStorage_APC = localStorage.getItem(APC_COMPARISON_SESSION_ID);

        const activePartNumbers = Object.keys(this.state.products).filter(
            (pn) =>
                partNumbersToRemove.find(pnToRemove => pnToRemove === pn) ===
                undefined,
        );

        if (contextRoot) {
            if (compareSessionId === compareSessionIdFromLocalStorage_APC) {
                localStorage.setItem(
                    APC_COMPARISON_LOCAL_STORAGE_NAME,
                    JSON.stringify(activePartNumbers)
                );
            }
        }
        else {
            if (compareSessionId === compareSessionIdFromLocalStorage) {
                localStorage.setItem(
                    `${COMPARISON_LOCAL_STORAGE_NAME}_${languageCode}_${countryCode.toUpperCase()}`,
                    activePartNumbers.join(LOCAL_STORAGE_PARTNUMBER_DEVIDER)
                );
            }
        }
        this.fetchProducts(activePartNumbers);
    }

    removeProducts = async (partNumbersToRemove: string[]) => {
        const {
            contextRoot,
            countryCode,
            languageCode,
            history,
            match: {
                params: { compareSessionId },
            },
        } = this.props;

        const compareSessionIdFromLocalStorage = localStorage.getItem(COMPARISON_SESSION_ID);
        const compareSessionIdFromLocalStorage_APC = localStorage.getItem(APC_COMPARISON_SESSION_ID);

        const activePartNumbers = this.props.match.params.skus.split(URL_PARTNUMBERS_DIVIDER).filter(
            (pn) =>
                partNumbersToRemove.find((pnToRemove) => pnToRemove === pn) ===
                undefined,
        );

        const newPartNumbersURL = activePartNumbers.join(
            URL_PARTNUMBERS_DIVIDER,
        );
        
        const baseURL = getComparatorPageURL({
            countryCode: this.props.countryCode,
            languageCode: this.props.languageCode,
            pageRoute: this.props.pageRoute,
        });
       
       if(!isEmpty(newPartNumbersURL)){
        history.push(`${baseURL}/${compareSessionId}/${newPartNumbersURL}/`)
       }else{
        history.push(`${baseURL}/${compareSessionId}/`)
       };

        if (contextRoot) {
            if (compareSessionId === compareSessionIdFromLocalStorage_APC) {
                localStorage.setItem(
                    APC_COMPARISON_LOCAL_STORAGE_NAME,
                    JSON.stringify(activePartNumbers)
                );
            }
        }
        else {
            if (compareSessionId === compareSessionIdFromLocalStorage) {
                localStorage.setItem(
                    `${COMPARISON_LOCAL_STORAGE_NAME}_${languageCode}_${countryCode.toUpperCase()}`,
                    activePartNumbers.join(LOCAL_STORAGE_PARTNUMBER_DEVIDER)
                );
            }
        }
        await this.fetchProducts(activePartNumbers);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onRemove = (partNumber: string) => {
        this.removeProducts([partNumber]);
    };

    clearComparisonList = () => {
        const {
            countryCode,
            languageCode,
            history,
            match: {
                params: { compareSessionId },
            },
        } = this.props;

        const baseURL = getComparatorPageURL({
            countryCode: this.props.countryCode,
            languageCode: this.props.languageCode,
            pageRoute: this.props.pageRoute,
        });
        history.push(`${baseURL}/${compareSessionId}/`);

        if (localStorage.getItem(APC_COMPARISON_LOCAL_STORAGE_NAME)) {
            localStorage.setItem(
                APC_COMPARISON_LOCAL_STORAGE_NAME,
                JSON.stringify([])
            );
        }

        const storageKey = `${COMPARISON_LOCAL_STORAGE_NAME}_${languageCode}_${countryCode.toUpperCase()}`

        if (localStorage.getItem(storageKey)) {
            localStorage.setItem(storageKey, '');
        }

        this.setState({
            products: {},
        });
    };

    toggleSectionHandler = currentStateKey => {
        this.setState({
            toggleSectionsState: {
                ...this.state.toggleSectionsState,
                [currentStateKey]: !this.state.toggleSectionsState[
                    currentStateKey
                ],
            },
        });
    };

    generateToggleSectionsState() {
        const { categories } = this.state;

        const toggleSectionsState = categories.reduce((acc, category) => {
            const { priority, name } = category;

            return {
                ...acc,
                [`${priority}_${name}`]: false,
            };
        }, {});

        this.setState({
            toggleSectionsState,
        });
    }

    renderContent(productsArray: MappedProduct[]) {
        const {
            contextRoot,
            countryCode,
            languageCode,
            match,
            bazaarVoiceEnabled,
            isPublic,
        } = this.props;
        const {
            categories,
            keyCategories,
            hideSimilarFeatures,
            highlightDifferences,
        } = this.state;

        if (this.state.isLoading) {
            return <Loader />;
        }

        const isDifferencesHighlighted =
            productsArray.length > 1 && highlightDifferences;

        const availableProducts = productsArray.filter(
            (product) => product.available === true,
        );
        const revertedProducts = [...availableProducts].reverse();
        const revertedProductsUIAAS = [
            ...this.state.productsUIAAS.filter(
                (product) => product.available === true,
            ),
        ].reverse();
        const revertedProductsUIAASinfo = [
            ...(this.state.productsUIAASinfo || []),
        ].reverse();

        if (!match.params.compareSessionId) {
            return (
                <NoItemsComponent
                    contextRoot={contextRoot}
                    countryCode={countryCode}
                    languageCode={languageCode}
                    isPublic={isPublic}
                />
            );
        }

        if (!availableProducts.length) {
            if (contextRoot) {
                localStorage.setItem(
                    APC_COMPARISON_LOCAL_STORAGE_NAME,
                    JSON.stringify([])
                );
            }
            else {
                localStorage.setItem(
                    `${COMPARISON_LOCAL_STORAGE_NAME}_${languageCode}_${countryCode.toUpperCase()}`,
                    ''
                );
            }
            window.scrollTo(0, 0);
        }

        return availableProducts.length ? (
            <div>
                <MediaQuery minDeviceWidth={DESKTOP_BREAKPOINT_MIN}>
                    <DesktopProducts
                        toggleSectionHandler={this.toggleSectionHandler}
                        toggleSectionState={this.state.toggleSectionsState}
                        products={revertedProducts}
                        productsUIAAS={revertedProductsUIAAS}
                        productsUIAASinfo={revertedProductsUIAASinfo}
                        myProducts={this.state.myProductsList}
                        commerceConnector={this.state.commerceConnector}
                        labels={this.state.labels}
                        downloadButtonLabels={this.state.downloadButtonLabels}
                        categories={categories}
                        onRemove={this.onRemove}
                        onUpdateMyProductsList={this.handleUpdateMyProductsList}
                        showDevPrioritiesInfo={this.state.showDevPrioritiesInfo}
                        keyCategories={keyCategories}
                        handleHideSimilarChange={this.handleHideSimilarChange}
                        hideSimilarFeatures={hideSimilarFeatures}
                        highlightDifferencesChange={
                            this.highlightDifferencesChange
                        }
                        highlightDifferences={isDifferencesHighlighted}
                        bazaarVoiceEnabled={bazaarVoiceEnabled}
                        productsRatings={this.state.productsRatings}
                        isAPCSite={this.state.isAPCSite}
                        apcCta={this.state.apcCta}
                        productAvailabilityInfo={
                            this.state.productAvailabilityInfo
                        }
                        runtimeInfo={this.state.runtimeInfo}
                        contextRoot={contextRoot}
                        countryCode={countryCode}
                        languageCode={languageCode}
                        linkClickHandlerCallback={this.sendDocumentDownloadEvent}
                    />
                </MediaQuery>
                <MediaQuery maxDeviceWidth={MOBILE_BREAKPOINT_MAX}>
                    <MobileProducts
                        products={revertedProducts}
                        productsUIAAS={revertedProductsUIAAS}
                        productsUIAASinfo={revertedProductsUIAASinfo}
                        labels={this.state.labels}
                        downloadButtonLabels={this.state.downloadButtonLabels}
                        myProducts={this.state.myProductsList}
                        commerceConnector={this.state.commerceConnector}
                        categories={categories}
                        keyCategories={keyCategories}
                        onRemove={this.onRemove}
                        onUpdateMyProductsList={this.handleUpdateMyProductsList}
                        toggleSectionHandler={this.toggleSectionHandler}
                        toggleSectionState={this.state.toggleSectionsState}
                        showDevPrioritiesInfo={this.state.showDevPrioritiesInfo}
                        handleHideSimilarChange={this.handleHideSimilarChange}
                        hideSimilarFeatures={hideSimilarFeatures}
                        highlightDifferencesChange={
                            this.highlightDifferencesChange
                        }
                        highlightDifferences={isDifferencesHighlighted}
                        bazaarVoiceEnabled={bazaarVoiceEnabled}
                        productsRatings={this.state.productsRatings}
                        isAPCSite={this.state.isAPCSite}
                        apcCta={this.state.apcCta}
                        productAvailabilityInfo={
                            this.state.productAvailabilityInfo
                        }
                        runtimeInfo={this.state.runtimeInfo}
                        contextRoot={contextRoot}
                        countryCode={countryCode}
                        languageCode={languageCode}
                        linkClickHandlerCallback={this.sendDocumentDownloadEvent}
                    />
                </MediaQuery>
            </div>
        ) : (
            <NoItemsComponent
                contextRoot={contextRoot}
                countryCode={countryCode}
                languageCode={languageCode}
                isPublic={isPublic}
            />
        );
    }

    clipBoardSuccessHandler = () => {
        const { notification } = this.props;

        notification.addNotification({
            status: EStatus.WARNING,
            message: this.props.intl.formatMessage({
                id: 'apc.comparison.link_copied_to_clipboard',
                defaultMessage: 'Link was copied to clipboard',
            }),
            buttonText: '',
        });
    };

    clipBoardErrorHandler = (e) => {
        // eslint-disable-next-line no-console
        console.error(e);
        const { notification } = this.props;

        notification.addNotification({
            status: EStatus.ERROR,
            message: this.props.intl.formatMessage({
                id: '',
                defaultMessage: 'Error while copying to buffer',
            }),
            buttonText: '',
        });
    };

    handleHideSimilarChange = () => {
        this.setState({
            hideSimilarFeatures: !this.state.hideSimilarFeatures,
        });
    };

    highlightDifferencesChange = () => {
        this.setState({
            highlightDifferences: !this.state.highlightDifferences,
        });
    };

    sendDocumentDownloadEvent = (link: string): void => {
        if (isAnalyticsEnabled()) {
          const documentId = getParamValueFromURL(link, 'p_Doc_Ref');
          const documentName = getParamValueFromURL(link, 'p_File_Name|p_Archive_Name|p_FileName') ?? '';
          const documentType = getParamValueFromURL(link, 'p_enDocType') ?? '';
      
          if (documentId) {
            send(documentDownloadEvent, { documentId, documentName, documentType });
          }
        }
      };

    render() {
        const {
            contextRoot,
            countryCode,
            languageCode,
            isPublic,
            match: {
                params: { compareSessionId },
            },
        } = this.props;

        let backToResultsLink;
        let showBackToResults;

        if (!contextRoot) {
            backToResultsLink = localStorage.getItem(`${languageCode}_${countryCode.toUpperCase()}-pesBackLink`);
            showBackToResults = backToResultsLink && (localStorage.getItem(COMPARISON_SESSION_ID) === compareSessionId);
        }
        const productsArray = this.getPartNumbers()
            .map((partNumber) => this.state.products[partNumber])
            .filter((prod) => Boolean(prod));

        return (
          <>
            <Page
              className={cn({
                [styles.comparePage]: true,
                [styles.comparePage_filled]: Boolean(productsArray.length),
                'products-container': true,
                apcComparison: true,
              })}
            >
              <div className={cn(styles.headingContainer, styles.headingContainer_actions)}>
                {showBackToResults && Boolean(productsArray.length) && (
                  <FormattedMessage
                    id='backToResults.label'
                    defaultMessage='Back to results'
                  >
                    {(txt) => (
                      <CustomLinkLeftArrow
                        href={backToResultsLink}
                        className={cn(
                          styles.backToResult,
                          'analytics-back-to-result'
                        )}
                      >
                        {txt}
                      </CustomLinkLeftArrow>
                    )}
                  </FormattedMessage>
                )}
              </div>
              <div
                className={cn(
                  styles.headingContainer,
                  styles.headingContainer_actions
                )}
              >
                <FormattedMessage id='comparator.display.text' defaultMessage='Compare Products'>
                  {(txt) => <h1 className={styles.heading}>{txt}</h1>}
                </FormattedMessage>
                {productsArray.filter((product) => product.available === true).length > 0 ? (
                  <div className={styles.actionsBar}>
                    <ExportTo
                      isPublic={isPublic}
                      cc={countryCode}
                      lc={languageCode}
                      partNumbersList={this.state.partNumbersList}
                      isApc={Boolean(contextRoot)}
                      storeData={this.state}
                    />
                    <div className={cn(styles.actionsBtn)}>
                      <FormattedMessage
                        id="comparison.label.clearList"
                        defaultMessage="Clear the List"
                      >
                        {txt => (
                          <QdsButton
                            onClick={this.clearComparisonList}
                            id='comparison.label.clearList'
                            className='analytics-compare-clear-list'
                            size='standard'
                            importance='subdued'
                            text={`${txt}`}
                            iconName='trash-can'
                          />
                        )}
                      </FormattedMessage>
                    </div>
                  </div>
                ) : null}
              </div>

              {this.state.isLoading ? <Loader /> : this.renderContent(productsArray)}
            </Page>
          </>
        );
    }
}

export default injectIntl(withNotificationStorageContext(withRouter(ComparePage)));
